import { AnalysisType, ElementPropertyToAnalysisType, GroupKey, projectAnalysis, ProjectAnalysisEvents, SankeyData, UnitType } from "@/types/analysis/ProjectAnalysis";
import type { ProjectAnalysisInput } from "@/types/analysis/ProjectAnalysisInput";
import { SankeyOptions } from "@/types/sankeyOptions";
import { ViewerIds } from "@/types/SelectionManager";
import { useProjectData } from "@api/analysis";
import { AppSettingType, CadModelCategory, Project, ScenarioMetadata } from "@api/apiClient";
import { useCadModelCategories } from "@api/cadModelCategories";
import { useCo2MaterialTypesByIds } from "@api/co2MaterialTypes";
import { useProject } from "@api/projects";
import { useAssignedCo2MaterialTypeIds } from "@features/analysis/hooks/useAssignedCo2MaterialTypeIds";
import useAppStore from "@hooks/useAppStore";
import useSelectionManager from "@hooks/useSelectionManager";
import i18n from "@i18n/i18n";
import { Box, LinearProgress, Typography, useTheme } from "@mui/material";
import { DefaultLink, DefaultNode, ResponsiveSankey, SankeyNodeDatum } from "@nivo/sankey";
import { Emitter, EventType } from "mitt";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SankeyLayerLabels from "./SankeyLayerLabels";

export interface SankeyChartProps {
  analysisInput?: ProjectAnalysisInput | null;
  width: number | undefined;
  height: number | undefined;
  options: SankeyOptions;
  emitter: Emitter<Record<EventType, unknown>>;
}

const SankeyChart = observer((props: SankeyChartProps) => {
  console.log("SankeyChart: ", props);
  const appStore = useAppStore();
  const selectionManager = useSelectionManager();
  const { id: projectId } = useParams();
  const [chartData, setChartData] = useState<any>({
    nodes: [],
    links: [],
  });
  const [layerLabels, setLayerLabels] = useState<string[]>([]);
  const project = useProject(projectId!);
  const projectData = useProjectData(projectId!);
  const assignedCo2MaterialTypeIds = useAssignedCo2MaterialTypeIds(props.analysisInput?.nodeMaterialLookup);
  const co2MaterialTypes = useCo2MaterialTypesByIds(assignedCo2MaterialTypeIds);

  useEffect(() => {
    const newLayerLabels: string[] = [];
    if (props.options.showScenarios) {
      newLayerLabels.push("Scenario");
    }
    if (props.options.showModels) {
      newLayerLabels.push("Models");
    }
    if (props.options.showCategories) {
      newLayerLabels.push("Categories");
    }
    if (props.options.showCadMaterials) {
      newLayerLabels.push("CAD Materials");
    }
    if (props.options.showCo2MaterialTypes) {
      newLayerLabels.push("EC Materials");
    }
    if (props.options.showSystems) {
      newLayerLabels.push("Systems");
    }
    if (props.options.showSubSystems) {
      newLayerLabels.push("SubSystems");
    }
    if (props.options.showAttributes) {
      newLayerLabels.push("Attributes");
    }
    setLayerLabels(newLayerLabels);
  }, [props.options]);

  useEffect(() => {
    updateChartData();
  }, []);

  useEffect(() => {
    const handleSankeyAnalysisChanged = async () => {
      updateChartData();
    };
    projectAnalysis.emitter.on(ProjectAnalysisEvents.SANKEY_ANALYSIS_CHANGED_EVENT, handleSankeyAnalysisChanged);

    return () => {
      projectAnalysis.emitter.off(ProjectAnalysisEvents.SANKEY_ANALYSIS_CHANGED_EVENT, handleSankeyAnalysisChanged);
    };
  }, []);

  const updateChartData = async () => {
    const analysisData = projectAnalysis.getAnalysis(AnalysisType.Sankey) as unknown as SankeyData;
    if (analysisData && analysisData.graph.links.some((link: any) => link.value > 0)) {
      for (var value of analysisData.graph.links as any[]) {
        value["value"] = applyUnitConversion(value["value"], projectAnalysis.selectedUnitType, value.scenarioMetadata);
      }
      // Validate and clean the graph data to prevent missing node errors
      const validatedGraph = validateSankeyData(analysisData.graph);
      setChartData(validatedGraph);
    } else {
      setChartData({
        nodes: [],
        links: [],
      });
    }
  };

  const applyUnitConversion = (value: number, selectedUnitType: UnitType, scenarioMetaData: ScenarioMetadata): number => {
    const totalFloorArea = scenarioMetaData.totalFloorArea || 1;
    const serviceLife = scenarioMetaData.serviceLife || 1;
    switch (selectedUnitType) {
      case UnitType.KgCO2ePerM2:
        return (value * 1000) / totalFloorArea;
      case UnitType.KgCO2ePerM2PerYear:
        return (value * 1000) / (totalFloorArea * serviceLife);
      case UnitType.tCO2e:
        return value; // No conversion
    }
  };

  const validateSankeyData = (graph: any) => {
    if (!graph || !graph.nodes || !graph.links) {
      return { nodes: [], links: [] };
    }

    // Create a set of valid node IDs
    const validNodeIds = new Set(graph.nodes.map((node: any) => node.id).filter((id: any) => id != null && id !== undefined));

    // Filter out links that reference non-existent nodes
    const validLinks = graph.links.filter((link: any) => {
      return validNodeIds.has(link.source) && validNodeIds.has(link.target) && link.value > 0;
    });

    // Filter out nodes that have no valid links (optional - you might want to keep isolated nodes)
    const nodesWithLinks = new Set();
    validLinks.forEach((link: any) => {
      nodesWithLinks.add(link.source);
      nodesWithLinks.add(link.target);
    });

    const validNodes = graph.nodes.filter(
      (node: any) => validNodeIds.has(node.id) && (nodesWithLinks.has(node.id) || graph.nodes.length <= 10), // Keep nodes if there are few total nodes
    );

    console.log("Sankey validation:", {
      originalNodes: graph.nodes.length,
      originalLinks: graph.links.length,
      validNodes: validNodes.length,
      validLinks: validLinks.length,
      removedLinks: graph.links.length - validLinks.length,
    });

    return {
      nodes: validNodes,
      links: validLinks,
    };
  };

  return (
    <Box sx={{ display: "flex", position: "relative", flexDirection: "column", flexGrow: 1, width: Number(props.width) - 8, height: Number(props.height) - 8 }}>
      <SankeyChartWithLabels
        analysisInput={props.analysisInput ?? null}
        project={project?.data?.project}
        layerLabels={layerLabels}
        showLayerTitles={props.options.showLayerTitles}
        graph={chartData}
        co2MaterialTypes={co2MaterialTypes.data}
        loading={projectData.isFetching || co2MaterialTypes.isFetching}
      />
    </Box>
  );
});

interface SankeyChartWithLabelsProps {
  analysisInput: ProjectAnalysisInput | null;
  project: Project | undefined;
  layerLabels: string[];
  showLayerTitles: boolean;
  graph: any;
  co2MaterialTypes: ReturnType<typeof useCo2MaterialTypesByIds>["data"];
  loading: boolean;
}

const SankeyChartWithLabels = observer((props: SankeyChartWithLabelsProps) => {
  console.log("SankeyChartWithLoader: ", props);
  const appStore = useAppStore();
  const theme = useTheme();
  const { id: projectId } = useParams();
  const selectionManager = useSelectionManager();
  const [showLayerTitles, setShowLayerTitles] = useState(props.showLayerTitles);
  const { layerLabels } = props;
  const hasChartData = props.graph.nodes.length > 0 && props.graph.links.length > 0;
  const cadModelCategories = useCadModelCategories();

  useEffect(() => {
    const showLayerTitles = props.showLayerTitles && props.graph?.links.some((link: any) => link.value > 0);
    setShowLayerTitles(showLayerTitles);
  }, [props.graph, props.showLayerTitles]);

  const handleModelVisibility = async (modelId: string) => {
    await appStore.lmViewer1?.turnOffAllVisibility();
    await appStore.lmViewer2?.turnOffAllVisibility();

    const urn = props.project?.getModelUrn(modelId);
    if (urn) {
      await appStore.lmViewer1?.turnOnAllVisibility(urn);
      await appStore.lmViewer2?.turnOnAllVisibility(urn);
    }
  };

  const handleIsolateByModelCategory = async (modelCategory: string) => {
    const cadModelCategory = cadModelCategories.data?.find((cmc: CadModelCategory) => cmc.label === modelCategory);
    await appStore.lmViewer1?.isolateByModelCategoryId(cadModelCategory?.value);
    await appStore.lmViewer2?.isolateByModelCategoryId(cadModelCategory?.value);
  };

  const handleIsolateByCadMaterial = async (cadMaterial: string) => {
    if (props.analysisInput) {
      projectAnalysis.addAnalysis(selectionManager, props.analysisInput, props.co2MaterialTypes, AnalysisType.CadMaterial);
    }
    projectAnalysis.getAnalysis(AnalysisType.CadMaterial)?.scenarioAnalyses.forEach((scenarioAnalysis) => {
      scenarioAnalysis.modelGroupCadIdsMap.forEach((groupCadIds, urn) => {
        (async () => {
          const viewerId = selectionManager.getViewerIdByScenarioId(scenarioAnalysis.scenarioId)!;
          if (viewerId === ViewerIds.Viewer1) {
            await appStore.lmViewer1?.isolateByCadIds(groupCadIds.get(cadMaterial)!, urn);
          } else {
            await appStore.lmViewer2?.isolateByCadIds(groupCadIds.get(cadMaterial)!, urn);
          }
        })();
      });
    });
  };

  const handleIsolateByCo2MaterialType = async (co2MaterialTypeId: string) => {
    if (props.analysisInput) {
      projectAnalysis.addAnalysis(selectionManager, props.analysisInput, props.co2MaterialTypes, AnalysisType.Co2MaterialType);
    }
    projectAnalysis.getAnalysis(AnalysisType.Co2MaterialType)?.scenarioAnalyses.forEach((scenarioAnalysis) => {
      scenarioAnalysis.modelGroupCadIdsMap.forEach((groupCadIds, urn) => {
        (async () => {
          const viewerId = selectionManager.getViewerIdByScenarioId(scenarioAnalysis.scenarioId)!;
          if (viewerId === ViewerIds.Viewer1) {
            await appStore.lmViewer1?.isolateByCadIds(groupCadIds.get(co2MaterialTypeId)!, urn);
          } else {
            await appStore.lmViewer2?.isolateByCadIds(groupCadIds.get(co2MaterialTypeId)!, urn);
          }
        })();
      });
    });
  };

  const handleIsolateBySystem = async (systemGuid: string) => {
    await appStore.lmViewer1?.isolateBySystem(systemGuid);
    await appStore.lmViewer2?.isolateBySystem(systemGuid);
  };

  const handleIsolateBySubSystem = async (subSystemGuid: string) => {
    await appStore.lmViewer1?.isolateBySubSystem(subSystemGuid);
    await appStore.lmViewer2?.isolateBySubSystem(subSystemGuid);
  };

  const handleIsolateByAttribute = async (attributeName: string, value: string) => {
    await appStore.lmViewer1?.isolateByAttributeValue(attributeName, value);
    await appStore.lmViewer2?.isolateByAttributeValue(attributeName, value);
  };

  const handleChartSelection = async (target: any, _event: any) => {
    console.log("handleChartSelection: ", target);

    if (target?.type === "modelId") {
      // Handle node selection on a Model node

      // source
      await handleModelVisibility(target?.id);

      // target
      const selections = target?.sourceLinks.reduce((acc: Set<GroupKey>, sourceLink: any) => {
        acc.add(sourceLink.target.id);
        return acc;
      }, new Set<GroupKey>());
      const analysisType = ElementPropertyToAnalysisType.get(target?.sourceLinks[0].targetType);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }
    } else if (target?.type === "cadModelCategoryId") {
      // Handle node selection on a CAD Model Category node

      // source
      await handleIsolateByModelCategory(target.label);

      // target
      const selections = new Set([target?.id]);
      const analysisType = ElementPropertyToAnalysisType.get(target?.type);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }
    } else if (target?.type === "cadMaterial") {
      // Handle node selection on a CAD Material node

      // source
      await handleIsolateByCadMaterial(target.label);

      // target
      const selections = new Set([target?.label]);
      const analysisType = ElementPropertyToAnalysisType.get(target?.type);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }
    } else if (target?.type === "co2MaterialTypeId") {
      // Handle node selection on a EC Material node

      // source
      await handleIsolateByCo2MaterialType(target.id);

      // target
      const selections = new Set([target.id]);
      const analysisType = ElementPropertyToAnalysisType.get(target.type);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }
    } else if (target?.type === "system") {
      // Handle node selection on a System node

      // source
      await handleIsolateBySystem(target.id);

      // target
      const selections = new Set([target.id]);
      const analysisType = ElementPropertyToAnalysisType.get(target.type);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }
    } else if (target?.type === "subSystem") {
      // Handle node selection on a SubSystem node

      // source
      await handleIsolateBySubSystem(target.id);

      // target
      const selections = new Set([target.id]);
      const analysisType = ElementPropertyToAnalysisType.get(target.type);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }
    } else if (target?.type === "freeText1" || target?.type === "freeText2" || target?.type === "freeText3") {
      // Handle node selection on an Attribute node

      // source
      await handleIsolateByAttribute(target.type, target.id);

      // target
      const selections = new Set([target.id]);
      const analysisType = ElementPropertyToAnalysisType.get(target.type);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }
    } else if (target?.sourceType === "modelId") {
      // Handle link selection on a Model link

      // source
      await handleModelVisibility(target?.source.id);

      // target
      const selections = new Set([target?.target.id]);
      const analysisType = ElementPropertyToAnalysisType.get(target?.target.type);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }
    } else if (target?.sourceType === "cadModelCategoryId") {
      // Handle link selection on a CAD Model Category link

      // source
      await handleIsolateByModelCategory(target.source.label);

      // target
      const selections = target?.source.sourceLinks.reduce((acc: Set<GroupKey>, sourceLink: any) => {
        acc.add(sourceLink.source.id);
        return acc;
      }, new Set<GroupKey>());
      const analysisType = ElementPropertyToAnalysisType.get(target?.source.sourceLinks[0].sourceType);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }
    } else if (target?.sourceType === "cadMaterial") {
      // Handle link selection on a CAD Material link

      // source
      handleIsolateByCadMaterial(target.source.id);

      let selections = new Set([target?.source.id]);
      let analysisType = ElementPropertyToAnalysisType.get(target?.source.type);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }

      // target
      selections = new Set([target?.target.id]);
      analysisType = ElementPropertyToAnalysisType.get(target?.target.type);
      if (analysisType) {
        projectAnalysis.addSelections(analysisType, selections);
      }
    } else {
      await appStore.lmViewer1?.resetView();
      await appStore.lmViewer2?.resetView();
    }
  };

  const customLegends = (props: any) => {
    if (!showLayerTitles) {
      return;
    }

    return (
      <SankeyLayerLabels
        nodes={props.nodes as SankeyNodeDatum<DefaultNode, DefaultLink>[]}
        layerLabels={layerLabels}
        labelOrientation="horizontal"
        getLabelTextColor={(node: SankeyNodeDatum<DefaultNode, DefaultLink>) => node.color} // revisit this
      />
    );
  };

  const renderSankeyChart = () => {
    try {
      if (!hasChartData) {
        return null;
      }

      return (
        <ResponsiveSankey
          data={props.graph}
          colors={(node: any) => node.nodeColor || "#8884d8"}
          label={(node: any) => node.label || "Unknown"}
          margin={{ top: showLayerTitles ? 24 : 8, bottom: 8 }}
          sort="descending"
          linkBlendMode={appStore.colorMode === "dark" ? "lighten" : "darken"}
          labelTextColor={{ from: "color", modifiers: [["brighter", 0.8]] }}
          theme={{
            tooltip: {
              container: {
                background: theme.palette.background.paper,
                color: theme.palette.text.primary,
              },
            },
          }}
          onClick={handleChartSelection}
          layers={["labels", "links", "nodes", "legends", customLegends as any]}
          valueFormat={(value: number) => i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits })}
          enableLinkGradient={true}
          linkHoverOpacity={0.6}
          nodeHoverOpacity={0.8}
        />
      );
    } catch (error) {
      console.error("Sankey chart rendering error:", error);
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: "text.secondary",
          }}
        >
          <Typography variant="body2">Error rendering Sankey chart. Please try refreshing the data.</Typography>
        </Box>
      );
    }
  };

  return (
    <>
      <Box sx={{ position: "absolute", top: 0, left: 0, width: "100%" }}>{props.loading && <LinearProgress />}</Box>
      {renderSankeyChart()}
    </>
  );
});

export default SankeyChart;
